import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { formularyApi, type FormularyListItem, type FormularyItem, type FormularyQueryParams } from '../../services/formularyService'

// Async thunks for API calls using proper HATEOAS navigation
export const fetchFormulary = createAsyncThunk(
  'formulary/fetchFormulary',
  async (params: FormularyQueryParams = {}, { getState }) => {
    console.log('[formularySlice] fetchFormulary thunk called', { params });
    const state = getState() as any
    const formularyHref = state.auth.currentClinic?.formularies?.href
    
    console.log('[formularySlice] Current clinic state:', state.auth.currentClinic);
    
    if (!formularyHref) {
      console.error('[formularySlice] No formulary href available');
      throw new Error('No clinic selected or clinic missing formulary link')
    }
    
    console.log('HATEOAS: Following formulary href:', formularyHref, 'with params:', params)
    
    // This is the HATEOAS way - use the href the API gave us
    const response = await formularyApi.getFormulary(formularyHref, params)
    return response
  }
)

// Server-side search for formulary with real-time debouncing
export const searchFormulary = createAsyncThunk(
  'formulary/searchFormulary',
  async (searchTerm: string, { getState }) => {
    const state = getState() as any
    const formularyHref = state.auth.currentClinic?.formularies?.href
    
    if (!formularyHref) {
      throw new Error('No clinic selected or clinic missing formulary link')
    }
    
    console.log('HATEOAS: Searching formulary with term:', searchTerm)
    
    // Use server-side search with search parameter and preserve other active filters
    const params: FormularyQueryParams = {}
    
    if (searchTerm.trim()) {
      params.name = [searchTerm]
    }
    
    // Include other active filters from state
    if (state.formulary.selectedCategories && state.formulary.selectedCategories.length > 0) {
      params.category = state.formulary.selectedCategories
    }
    if (state.formulary.selectedForms && state.formulary.selectedForms.length > 0) {
      params.form = state.formulary.selectedForms
    }
    if (state.formulary.selectedRoutes && state.formulary.selectedRoutes.length > 0) {
      params.route = state.formulary.selectedRoutes
    }
    if (state.formulary.selectedPharmacies && state.formulary.selectedPharmacies.length > 0) {
      params.pharmacy = state.formulary.selectedPharmacies
    }
    // Note: pharmacy filter would need to be added to formulary state if we want to preserve it during search
    
    const response = await formularyApi.getFormulary(formularyHref, params)
    return response
  }
)

// Fetch formulary for a specific page using HATEOAS URLs or page number
export const fetchFormularyPage = createAsyncThunk(
  'formulary/fetchFormularyPage',
  async (params: { page?: number; hateoasUrl?: string }, { getState }) => {
    const state = getState() as any
    
    let url: string
    let queryParams: FormularyQueryParams = {}
    
    if (params.hateoasUrl) {
      // Use the HATEOAS URL, but replace {cid} placeholder with actual clinic ID
      url = params.hateoasUrl
      
      // Fix backend bug: Replace {cid} placeholder with actual clinic ID
      const clinicId = state.auth.currentClinic?.id
      if (clinicId && url.includes('{cid}')) {
        url = url.replace('{cid}', clinicId)
        console.log('HATEOAS: Replaced {cid} placeholder with clinic ID:', clinicId)
      }
      
      console.log('HATEOAS: Following formulary pagination URL:', url)
    } else if (params.page) {
      // Use page number with base formulary href
      const formularyHref = state.auth.currentClinic?.formularies?.href
      
      if (!formularyHref) {
        throw new Error('No clinic selected or clinic missing formularies link')
      }
      
      url = formularyHref
      queryParams = { 
        page_number: params.page,
        page_size: state.formulary.pageSize 
      }
      
      // Include active filters
      if (state.formulary.selectedCategories?.length > 0) {
        queryParams.category = state.formulary.selectedCategories
      }
      if (state.formulary.selectedForms?.length > 0) {
        queryParams.form = state.formulary.selectedForms
      }
      if (state.formulary.selectedRoutes?.length > 0) {
        queryParams.route = state.formulary.selectedRoutes
      }
      if (state.formulary.selectedPharmacies?.length > 0) {
        queryParams.pharmacy = state.formulary.selectedPharmacies
      }
      
      console.log('HATEOAS: Fetching formulary page', params.page, 'with params:', queryParams)
    } else {
      throw new Error('Either page number or HATEOAS URL must be provided')
    }
    
    const response = await formularyApi.getFormulary(url, queryParams)
    return response
  }
)

// State interface
interface FormularyState {
  items: FormularyListItem[]
  currentItem: FormularyItem | null
  totalItems: number
  loading: boolean
  error: string | null
  searchTerm: string
  isSearching: boolean
  // Filters (arrays for multi-select)
  selectedCategories: string[]
  selectedForms: string[]
  selectedRoutes: string[]
  selectedPharmacies: string[]
  // HATEOAS pagination state
  pageSize: number
  pageNumber: number
  pagePrevious: string | null
  pageNext: string | null
}

// Add async thunk for fetching individual formulary item
export const fetchFormularyItem = createAsyncThunk(
  'formulary/fetchFormularyItem',
  async (itemHref: string) => {
    console.log('HATEOAS: Fetching individual formulary item:', itemHref)
    const response = await formularyApi.getFormularyItem(itemHref)
    return response
  }
)

// Initial state
const initialState: FormularyState = {
  items: [],
  currentItem: null,
  totalItems: 0,
  loading: false,
  error: null,
  searchTerm: '',
  isSearching: false,
  selectedCategories: [],
  selectedForms: [],
  selectedRoutes: [],
  selectedPharmacies: [],
  pageSize: 10,
  pageNumber: 1,
  pagePrevious: null,
  pageNext: null,
}

// Slice
const formularySlice = createSlice({
  name: 'formulary',
  initialState,
  reducers: {
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload
    },
    clearError: (state) => {
      state.error = null
    },
    setSelectedCategories: (state, action) => {
      state.selectedCategories = action.payload
    },
    setSelectedForms: (state, action) => {
      state.selectedForms = action.payload
    },
    setSelectedRoutes: (state, action) => {
      state.selectedRoutes = action.payload
    },
    setSelectedPharmacies: (state, action) => {
      state.selectedPharmacies = action.payload
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload
    },
    setPageNumber: (state, action) => {
      state.pageNumber = action.payload
    },
    clearCurrentItem: (state) => {
      state.currentItem = null
    },
  },
  extraReducers: (builder) => {
    // Fetch formulary
    builder
      .addCase(fetchFormulary.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchFormulary.fulfilled, (state, action) => {
        state.loading = false
        state.items = action.payload.items
        state.totalItems = action.payload.total_items
        state.pageSize = action.payload.page_size || state.pageSize
        state.pageNumber = action.payload.page_number || state.pageNumber
        state.pagePrevious = action.payload.page_previous
        state.pageNext = action.payload.page_next
      })
      .addCase(fetchFormulary.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch formulary'
      })

    // Fetch individual formulary item
    builder
      .addCase(fetchFormularyItem.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchFormularyItem.fulfilled, (state, action) => {
        state.loading = false
        state.currentItem = action.payload
      })
      .addCase(fetchFormularyItem.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch formulary item'
      })

    // Search formulary
    builder
      .addCase(searchFormulary.pending, (state) => {
        state.isSearching = true
        state.error = null
      })
      .addCase(searchFormulary.fulfilled, (state, action) => {
        state.isSearching = false
        state.items = action.payload.items
        state.totalItems = action.payload.total_items
        state.pageSize = action.payload.page_size || state.pageSize
        state.pageNumber = action.payload.page_number || state.pageNumber
        state.pagePrevious = action.payload.page_previous
        state.pageNext = action.payload.page_next
      })
      .addCase(searchFormulary.rejected, (state, action) => {
        state.isSearching = false
        state.error = action.error.message || 'Failed to search formulary'
      })

    // Fetch formulary page
    builder
      .addCase(fetchFormularyPage.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchFormularyPage.fulfilled, (state, action) => {
        state.loading = false
        state.items = action.payload.items
        state.totalItems = action.payload.total_items
        state.pageSize = action.payload.page_size || state.pageSize
        state.pageNumber = action.payload.page_number || state.pageNumber
        state.pagePrevious = action.payload.page_previous
        state.pageNext = action.payload.page_next
      })
      .addCase(fetchFormularyPage.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch formulary page'
      })
  },
})

// Export actions
export const { 
  setSearchTerm, 
  clearError, 
  setSelectedCategories, 
  setSelectedForms, 
  setSelectedRoutes, 
  setSelectedPharmacies,
  setPageSize, 
  setPageNumber,
  clearCurrentItem
} = formularySlice.actions

// Selectors
export const selectFormularyItems = (state: { formulary: FormularyState }) => state.formulary.items
export const selectCurrentFormularyItem = (state: { formulary: FormularyState }) => state.formulary.currentItem
export const selectFormularyTotal = (state: { formulary: FormularyState }) => state.formulary.totalItems
export const selectFormularyLoading = (state: { formulary: FormularyState }) => state.formulary.loading
export const selectFormularyError = (state: { formulary: FormularyState }) => state.formulary.error
export const selectSearchTerm = (state: { formulary: FormularyState }) => state.formulary.searchTerm
export const selectIsSearching = (state: { formulary: FormularyState }) => state.formulary.isSearching
export const selectSelectedCategories = (state: { formulary: FormularyState }) => state.formulary.selectedCategories
export const selectSelectedForms = (state: { formulary: FormularyState }) => state.formulary.selectedForms
export const selectSelectedRoutes = (state: { formulary: FormularyState }) => state.formulary.selectedRoutes
export const selectSelectedPharmacies = (state: { formulary: FormularyState }) => state.formulary.selectedPharmacies
export const selectPageSize = (state: { formulary: FormularyState }) => state.formulary.pageSize
export const selectPageNumber = (state: { formulary: FormularyState }) => state.formulary.pageNumber
export const selectPagePrevious = (state: { formulary: FormularyState }) => state.formulary.pagePrevious
export const selectPageNext = (state: { formulary: FormularyState }) => state.formulary.pageNext

// Pagination data selector for use with PaginationControls
export const selectFormularyPagination = (state: { formulary: FormularyState }) => ({
  currentPage: state.formulary.pageNumber,
  pageSize: state.formulary.pageSize,
  totalItems: state.formulary.totalItems,
  totalPages: Math.ceil(state.formulary.totalItems / state.formulary.pageSize),
  hasNextPage: !!state.formulary.pageNext,
  hasPreviousPage: !!state.formulary.pagePrevious,
  pageNext: state.formulary.pageNext,
  pagePrevious: state.formulary.pagePrevious,
})

// Since we now use server-side search and filtering, this selector just returns the items
export const selectFilteredFormulary = (state: { formulary: FormularyState }) => {
  return state.formulary.items
}

export default formularySlice.reducer